// packages/web-ui/src/sse/client.ts
//
// W2-S5 AC #10 — Thin wrapper around `@microsoft/fetch-event-source`.
//
// Why fetch-event-source over the native `EventSource`? Native is GET-only
// and cannot send custom headers; the server requires `Authorization: Bearer
// <token>` so we MUST use `fetch-event-source` (Arch §8.5 explicit, Pitfall #1).
//
// The implementation is injected via `fetchEventSourceImpl` so unit tests
// can drive a fixture stream without a real server. Returns a `{ abort }`
// handle so the caller (the React `<SseProvider>`) can own the lifecycle
// and abort on unmount (Arch AR-8 line 1884).

import {
  fetchEventSource,
  type FetchEventSourceInit,
  type EventSourceMessage,
} from '@microsoft/fetch-event-source';

/** Concrete shape that calls the upstream library. */
export type FetchEventSourceImpl = (
  url: string,
  init: FetchEventSourceInit,
) => Promise<void>;

export interface OpenSseStreamOptions {
  /** Endpoint URL — typically `/api/v1/chat/stream?sessionId=...&since=...`. */
  url: string;
  /** Bearer token; null skips the header. */
  token: string | null;
  /** Replay cursor (last event id seen). */
  since?: string | null;
  /** Per-event callback. */
  onEvent: (event: SseEvent) => void;
  /** Open / close lifecycle hooks. */
  onOpen?: () => void;
  /** Called for every error (each call increments the slice's attempt count). */
  onError?: (err: unknown) => void;
  /** Called when the library has stopped retrying (custom 10-attempt cap). */
  onExhausted?: () => void;
  /** Override the upstream library (tests inject `FixtureStreamProvider`). */
  fetchEventSourceImpl?: FetchEventSourceImpl;
  /** Maximum retries before giving up. Defaults to 10 (FR-SHELL-8 + AC #10). */
  maxRetries?: number;
  /** Retry delay calculator (ms). Defaults to capped exponential. */
  retryDelay?: (attempt: number) => number;
}

export interface SseEvent {
  /** Event name from the SSE `event:` field. */
  event: string;
  /** Parsed `data:` payload as JSON, OR the raw string if JSON parse failed. */
  data: unknown;
  /** Optional `id:` field for replay. */
  id?: string;
}

export interface SseStreamHandle {
  abort: () => void;
}

/** Default capped exponential backoff: 1s, 2s, 4s, ..., capped at 30s. */
export function defaultRetryDelay(attempt: number): number {
  return Math.min(30_000, 1_000 * Math.pow(2, attempt));
}

/**
 * Open the SSE stream. Returns a handle whose `abort()` closes the stream.
 *
 * Each `onmessage` callback parses `msg.data` as JSON; on parse failure the
 * raw string is forwarded so the caller can decide whether to drop the event.
 *
 * The library auto-retries on close with the configured `retryDelay`. The
 * handler increments the slice's attempt counter via `onError` and gives up
 * after `maxRetries` consecutive failures by throwing a sentinel inside
 * `onerror` — fetch-event-source treats a thrown error as terminal.
 */
export function openSseStream(options: OpenSseStreamOptions): SseStreamHandle {
  const ctrl = new AbortController();
  const fetchImpl = options.fetchEventSourceImpl ?? fetchEventSource;
  const maxRetries = options.maxRetries ?? 10;
  const retryDelay = options.retryDelay ?? defaultRetryDelay;
  let attempts = 0;
  let exhausted = false;

  const headers: Record<string, string> = { Accept: 'text/event-stream' };
  if (options.token !== null) {
    headers.Authorization = `Bearer ${options.token}`;
  }

  // `since` is forwarded as a query parameter when provided.
  let url = options.url;
  if (options.since !== null && options.since !== undefined && options.since.length > 0) {
    const sep = url.includes('?') ? '&' : '?';
    url = `${url}${sep}since=${encodeURIComponent(options.since)}`;
  }

  void fetchImpl(url, {
    method: 'GET',
    headers,
    signal: ctrl.signal,
    openWhenHidden: true,
    onopen: async (response: Response) => {
      if (!response.ok) {
        // Treat non-2xx as a retryable error.
        throw new Error(`sse open failed: ${response.status}`);
      }
      attempts = 0;
      exhausted = false;
      options.onOpen?.();
    },
    onmessage: (msg: EventSourceMessage) => {
      let parsed: unknown = msg.data;
      try {
        parsed = msg.data.length > 0 ? JSON.parse(msg.data) : null;
      } catch {
        // Keep `parsed` as the raw string; the caller can decide.
      }
      options.onEvent({
        event: msg.event !== undefined && msg.event.length > 0 ? msg.event : 'message',
        data: parsed,
        id: msg.id,
      });
    },
    onerror: (err: unknown) => {
      attempts += 1;
      options.onError?.(err);
      if (attempts > maxRetries) {
        exhausted = true;
        options.onExhausted?.();
        // Throwing tells fetch-event-source to STOP retrying.
        throw err instanceof Error ? err : new Error('sse exhausted');
      }
      // Returning a number tells the library to wait that long before retrying.
      return retryDelay(attempts - 1);
    },
    onclose: () => {
      // Library will respect onerror's terminal throw; close is reached
      // either after a graceful server close (rare) or after exhaustion.
      if (!exhausted) {
        options.onError?.(new Error('sse closed'));
      }
    },
  }).catch((err: unknown) => {
    // Bubble the final terminal error up via onError — already invoked, but
    // we swallow here so the promise chain doesn't leak unhandled rejection.
    options.onError?.(err);
  });

  return {
    abort: () => {
      ctrl.abort();
    },
  };
}
